import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { BellRing, ChevronDown, ChevronUp } from 'lucide-react';
import { PERMISSIONS } from '@nova/shared';
import { Button } from '@/components/ui';
import { ApiError, refreshAccessToken } from '@/lib/api';
import { NOTIFICATION_COUNTS_KEY } from '@/lib/query-client';
import { useAuthStore } from '@/stores/auth.store';
import { useLang } from '@/i18n';
import { useFeatureEnabled, useOnlineBookingCounts } from './hooks';
import { onlineBookingApi, onlineBookingKeys } from './api';
import type { OnlineBookingRow } from './api';
import { OnlineBookingReviewDrawer } from './OnlineBookingReviewDrawer';

export const onlineBookingPreferenceKey = (userId: string) => `nova-online-booking-preferences:${userId}`;

interface ReviewOpenDetail { appointmentId: string; errorCode?: string }

export function OnlineBookingChrome() {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.accessToken);
  const canAny = useAuthStore((s) => s.canAny);
  const canView = canAny(PERMISSIONS.ONLINE_BOOKING_VIEW);
  const canReview = canAny(PERMISSIONS.ONLINE_BOOKING_REVIEW);
  const enabled = useFeatureEnabled('onlineBooking');
  const featureOn = enabled.data?.onlineBooking === true;
  const countsQuery = useOnlineBookingCounts(featureOn && canView);
  const counts = countsQuery.data ?? { pending: 0, overdue: 0, serverNow: '' };
  const threshold = enabled.data?.responseThresholdMinutes ?? 10;
  const lang = useLang();
  const { t } = useTranslation('onlineBooking');
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const lastEventId = useRef('');
  const invalidator = useRef<() => void>(() => undefined);
  const priorPendingIds = useRef<Set<string> | null>(null);
  const createdEventIds = useRef(new Set<string>());
  const toastedArrivalIds = useRef(new Set<string>());
  const [activeRequestId, setActiveRequestId] = useState<string | null>(null);
  const [drawerErrorCode, setDrawerErrorCode] = useState<string | undefined>();
  const [collapsed, setCollapsed] = useState(false);
  const [preferences, setPreferences] = useState({ sound: false, desktop: false });
  const [clock, setClock] = useState(Date.now());
  const pendingQuery = useQuery({
    queryKey: onlineBookingKeys.list('pending', undefined, undefined, 100),
    queryFn: fetchPendingSnapshot,
    enabled: featureOn && canView,
    refetchInterval: 60_000,
  });
  const pendingSnapshot = pendingQuery.data;
  const serverOffset = pendingSnapshot
    ? Date.parse(pendingSnapshot.serverNow) - pendingQuery.dataUpdatedAt
    : counts.serverNow
      ? Date.parse(counts.serverNow) - (countsQuery.dataUpdatedAt ?? Date.now())
      : 0;
  const serverNow = clock + serverOffset;

  invalidator.current = () => {
    void qc.invalidateQueries({ queryKey: onlineBookingKeys.all });
    void qc.invalidateQueries({ queryKey: NOTIFICATION_COUNTS_KEY });
    void qc.invalidateQueries({ queryKey: ['calendar'] });
    void qc.invalidateQueries({ queryKey: ['appointments'] });
  };

  useEffect(() => {
    if (!user?.id) return;
    try {
      const saved = localStorage.getItem(onlineBookingPreferenceKey(user.id));
      if (saved) setPreferences(JSON.parse(saved) as { sound: boolean; desktop: boolean });
    } catch { /* malformed local preferences are ignored */ }
  }, [user?.id]);

  useEffect(() => {
    const update = () => {
      if (!user?.id) return;
      try {
        const saved = localStorage.getItem(onlineBookingPreferenceKey(user.id));
        if (saved) setPreferences(JSON.parse(saved) as { sound: boolean; desktop: boolean });
      } catch { /* malformed local preferences are ignored */ }
    };
    window.addEventListener('online-booking-preferences', update);
    return () => window.removeEventListener('online-booking-preferences', update);
  }, [user?.id]);

  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const open = (event: Event) => {
      const detail = (event as CustomEvent<ReviewOpenDetail>).detail;
      if (!detail?.appointmentId) return;
      setDrawerErrorCode(detail.errorCode);
      setActiveRequestId(detail.appointmentId);
    };
    const close = () => {
      setActiveRequestId(null);
      setDrawerErrorCode(undefined);
      if (location.pathname === '/online-bookings' && new URLSearchParams(location.search).has('request')) {
        const params = new URLSearchParams(location.search);
        params.delete('request');
        navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : '' }, { replace: true });
      }
    };
    window.addEventListener('online-booking-review-open', open);
    window.addEventListener('online-booking-review-close', close);
    const requestId = new URLSearchParams(location.search).get('request');
    if (location.pathname === '/online-bookings') {
      if (requestId) setActiveRequestId(requestId);
      else {
        setActiveRequestId(null);
        setDrawerErrorCode(undefined);
      }
    }
    return () => {
      window.removeEventListener('online-booking-review-open', open);
      window.removeEventListener('online-booking-review-close', close);
    };
  }, [location.pathname, location.search, navigate]);

  // Authenticated fetch-based SSE uses the same bearer token and refresh flow as Axios.
  useEffect(() => {
    if (!featureOn || !canView || !token) return;
    let stopped = false;
    let retry = 1000;
    let controller: AbortController | null = null;
    const syncOnVisible = () => { if (document.visibilityState === 'visible') invalidator.current(); };
    document.addEventListener('visibilitychange', syncOnVisible);

    const connect = async () => {
      while (!stopped) {
        controller = new AbortController();
        try {
          let bearer = useAuthStore.getState().accessToken;
          let response = await fetch('/api/events', {
            headers: { Authorization: `Bearer ${bearer}`, Accept: 'text/event-stream', ...(lastEventId.current ? { 'Last-Event-ID': lastEventId.current } : {}) },
            signal: controller.signal,
          });
          if (response.status === 401) {
            bearer = await refreshAccessToken();
            if (!bearer) break;
            response = await fetch('/api/events', {
              headers: { Authorization: `Bearer ${bearer}`, Accept: 'text/event-stream', ...(lastEventId.current ? { 'Last-Event-ID': lastEventId.current } : {}) },
              signal: controller.signal,
            });
          }
          if (!response.ok || !response.body) throw new Error(`SSE ${response.status}`);
          retry = 1000;
          invalidator.current();
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let eventName = 'message';
          let eventId = '';
          let data = '';
          while (!stopped) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let boundary = buffer.indexOf('\n');
            while (boundary >= 0) {
              const line = buffer.slice(0, boundary).replace(/\r$/, '');
              buffer = buffer.slice(boundary + 1);
              if (!line) {
                if (eventId) lastEventId.current = eventId;
                if (eventName === 'reset') lastEventId.current = '';
                if (eventName === 'onlineBooking.created') {
                  try {
                    const payload = JSON.parse(data) as { entityId?: string };
                    if (payload.entityId) createdEventIds.current.add(payload.entityId);
                  } catch { /* database sync still recovers malformed event data */ }
                }
                if (eventName !== 'message' || data) invalidator.current();
                eventName = 'message'; eventId = ''; data = '';
              } else if (!line.startsWith(':')) {
                const colon = line.indexOf(':');
                const key = colon < 0 ? line : line.slice(0, colon);
                const val = colon < 0 ? '' : line.slice(colon + 1).replace(/^ /, '');
                if (key === 'event') eventName = val;
                if (key === 'id') eventId = val;
                if (key === 'data') data += `${val}\n`;
              }
              boundary = buffer.indexOf('\n');
            }
          }
          invalidator.current();
        } catch (error) {
          if (stopped || (error instanceof DOMException && error.name === 'AbortError')) break;
        }
        if (!stopped) {
          await new Promise((resolve) => window.setTimeout(resolve, retry));
          retry = Math.min(30_000, retry * 2);
          invalidator.current();
        }
      }
    };
    void connect();
    return () => { stopped = true; controller?.abort(); document.removeEventListener('visibilitychange', syncOnVisible); };
  }, [featureOn, canView, token, qc]);

  const showArrival = (row: OnlineBookingRow) => {
    if (row.claimHolder && row.claimHolder.id !== user?.id) return;
    if (toastedArrivalIds.current.has(row.appointmentId)) return;
    toastedArrivalIds.current.add(row.appointmentId);
    if (preferences.sound) playReceptionTone();
    if (preferences.desktop && 'Notification' in window && Notification.permission === 'granted') {
      new Notification(t('toast.newTitle'), { body: `${row.submittedName} · ${row.rawPhone}` });
    }
    toast.custom((id) => (
      <div data-testid="online-booking-arrival-toast" className="w-[min(26rem,calc(100vw-2rem))] rounded-lg border border-slate-200 bg-white p-3 shadow-xl" dir={lang === 'AR' ? 'rtl' : 'ltr'}>
        <div className="flex items-start gap-2"><BellRing className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" /><div className="min-w-0 flex-1"><strong className="block text-sm">{t('toast.newTitle')}</strong><span className="text-xs text-slate-600">{row.submittedName} · {row.rawPhone}</span></div></div>
        <div className="mt-3 flex gap-2"><Button size="sm" onClick={() => { toast.dismiss(id); openRequest(row.appointmentId); }}>{t('toast.review')}</Button>{row.appointment.patientId ? <Button size="sm" variant="secondary" onClick={() => { toast.dismiss(id); void onlineBookingApi.confirm(row.appointmentId, { version: row.version }).then(invalidator.current).catch((error: unknown) => { const code = error instanceof ApiError ? error.code : ''; if (code === 'staleVersion' || code === 'slotConflict') openRequest(row.appointmentId, code); }); }}>{t('toast.confirm')}</Button> : null}</div>
      </div>
    ), { id: `online-arrival-${row.appointmentId}`, duration: Infinity, closeButton: true });
  };

  // Use the pending-list snapshot as the source of arrivals. This detects a new
  // request even when one request was handled and the pending count is unchanged.
  useEffect(() => {
    if (!featureOn || !canReview || !pendingSnapshot) return;
    const currentIds = new Set(pendingSnapshot.data.map((row) => row.appointmentId));
    if (priorPendingIds.current) {
      for (const row of pendingSnapshot.data) {
        if (!priorPendingIds.current.has(row.appointmentId) || createdEventIds.current.has(row.appointmentId)) showArrival(row);
      }
    } else {
      for (const row of pendingSnapshot.data) {
        if (createdEventIds.current.has(row.appointmentId)) showArrival(row);
      }
    }
    priorPendingIds.current = currentIds;
    for (const id of createdEventIds.current) if (currentIds.has(id)) createdEventIds.current.delete(id);
  }, [featureOn, canReview, pendingSnapshot, preferences, user?.id]);

  useEffect(() => {
    if (!featureOn || !canReview || !pendingSnapshot?.data.length) return;
    const thresholdMs = threshold * 60_000;
    for (const row of pendingSnapshot.data) {
      const age = serverNow - Date.parse(row.createdAt);
      if (age < thresholdMs || (row.claimHolder && row.claimHolder.id !== user?.id)) continue;
      const reminder = Math.floor(age / thresholdMs);
      const key = `nova-ob-overdue:${row.appointmentId}:${reminder}`;
      if (sessionStorage.getItem(key)) continue;
      setCollapsed(false);
      sessionStorage.setItem(key, '1');
      if (preferences.sound) playReceptionTone();
      if (preferences.desktop && 'Notification' in window && Notification.permission === 'granted') {
        new Notification(t('toast.overdueTitle'), { body: row.submittedName });
      }
      toast.custom(() => <div data-testid="online-booking-overdue-toast" role="status" aria-live="assertive" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm shadow-xl">{t('toast.overdueReminder')} · {row.submittedName}<button type="button" className="ms-2 font-bold underline" onClick={() => openRequest(row.appointmentId)}>{t('toast.review')}</button></div>, { id: `online-overdue-${row.appointmentId}-${reminder}`, duration: Infinity, closeButton: true });
    }
  }, [featureOn, canReview, pendingSnapshot, serverNow, threshold, user?.id, preferences, t]);

  useEffect(() => {
    const original = document.title.replace(/^(?:!\d+\s+)?(?:\(\d+\)\s*)?/, '');
    if (featureOn && canView && counts.pending) document.title = `${counts.overdue ? `!${counts.overdue} ` : ''}(${counts.pending}) ${original}`;
    else document.title = original;
    return () => { document.title = original; };
  }, [featureOn, canView, counts.pending, counts.overdue]);

  const localOverdue = pendingSnapshot?.data.filter((row) => serverNow - Date.parse(row.createdAt) >= threshold * 60_000).length ?? 0;
  const overdueRows = useMemo(() => Math.max(counts.overdue, localOverdue), [counts.overdue, localOverdue]);
  if (!featureOn || !canView) return null;

  return <>
    {canReview && overdueRows > 0 && !collapsed ? <div data-testid="online-booking-overdue-banner" role="status" aria-live="polite" className="flex min-h-10 items-center gap-2 bg-amber-100 px-4 py-2 text-sm text-amber-950 max-lg:px-2 max-lg:text-xs">
      <span className="min-w-0 flex-1">{t('banner.overdue', { count: overdueRows, threshold })}</span>
      <Link to="/online-bookings?tab=overdue" className="shrink-0 font-bold underline">{t('toast.review')}</Link>
      <button data-testid="online-booking-overdue-banner-collapse" type="button" aria-label={t('banner.collapse')} onClick={() => setCollapsed(true)} className="flex h-8 w-8 items-center justify-center rounded hover:bg-amber-200"><ChevronUp className="h-4 w-4" /></button>
    </div> : null}
    {canReview && overdueRows > 0 && collapsed ? <button data-testid="online-booking-overdue-banner-expand" type="button" aria-label={t('banner.expand')} onClick={() => setCollapsed(false)} className="flex h-7 w-full items-center justify-center bg-amber-100 text-xs text-amber-900"><ChevronDown className="h-4 w-4" /><span className="sr-only">{t('banner.expand')}</span></button> : null}
    <OnlineBookingReviewDrawer requestId={activeRequestId} canReview={canReview} initialErrorCode={drawerErrorCode} onClose={() => window.dispatchEvent(new Event('online-booking-review-close'))} />
  </>;
}

function openRequest(appointmentId: string, errorCode?: string) {
  window.dispatchEvent(new CustomEvent('online-booking-review-open', { detail: { appointmentId, errorCode } satisfies ReviewOpenDetail }));
}

async function fetchPendingSnapshot() {
  const first = await onlineBookingApi.list({ tab: 'pending', page: 1, pageSize: 100 });
  if (first.pagination.totalPages <= 1) return first;
  const rest = await Promise.all(
    Array.from({ length: first.pagination.totalPages - 1 }, (_, index) =>
      onlineBookingApi.list({ tab: 'pending', page: index + 2, pageSize: 100 }),
    ),
  );
  return { ...first, data: [...first.data, ...rest.flatMap((page) => page.data)] };
}

function playReceptionTone() {
  const AudioContextCtor = window.AudioContext;
  if (!AudioContextCtor) return;
  const context = new AudioContextCtor();
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.frequency.value = 740;
  gain.gain.value = 0.06;
  oscillator.connect(gain); gain.connect(context.destination);
  oscillator.start(); oscillator.stop(context.currentTime + 0.16);
  oscillator.onended = () => { void context.close(); };
}
